import { asyncRoutes, constantRoutes } from '@/router';
import _ from 'lodash';

function canAccess(roles = [], permissions = [], route, isExtranet = false) {
  if (!route?.meta) return true;
  // Routes publiques toujours accessibles
  if (route.meta.public) return true;

  const routeRoles = route.meta.roles || [];
  const routePermissions = route.meta.permissions || [];

  // `roles: ['extranet client']` marque une route comme réservée au portail
  // client. Seule la lecture du CÔTÉ change ici : `isExtranet` est calculé par
  // le serveur (`UserResource.scope`) au lieu d'être deviné par la présence d'un
  // rôle nommé, qui n'est plus qu'un profil extranet parmi d'autres.
  //
  // Le comportement, lui, reste identique — y compris le fait que les
  // permissions déclarées sur une route extranet ne soient pas évaluées ici.
  // Les faire jouer serait le filtrage attendu une fois les profils en place,
  // mais aujourd'hui `extranet client` porte zéro permission en preprod : le
  // menu du portail s'y viderait entièrement. Ce durcissement doit arriver AVEC
  // les profils et leurs permissions, pas avant. Idem pour la fermeture des
  // routes internes aux clients : cinq modules (documentation, compareCA,
  // gestionNumFixe, renouvelement, error) ne déclarent aucune garde et leur
  // sont aujourd'hui visibles.
  if (routeRoles.includes('extranet client')) return isExtranet;

  if (roles.includes('super admin')) return true;

  const hasRole = !routeRoles.length || roles.some(r => routeRoles.includes(r));
  const hasPermission = !routePermissions.length || permissions.some(p => routePermissions.includes(p));

  return hasRole && hasPermission;
}

function filterAsyncRoutes(routes = [], roles = [], permissions = [], isExtranet = false) {
  return routes.reduce((acc, route) => {
    if (!route) return acc;
    const tmp = _.cloneDeep(route);

    if (canAccess(roles, permissions, tmp, isExtranet)) {
      if (tmp.children?.length) tmp.children = filterAsyncRoutes(tmp.children, roles, permissions, isExtranet);
      // Un nœud de regroupement sidebar vidé de tous ses modules par le filtrage
      // ne doit pas afficher de sous-menu vide.
      if (tmp.meta?.is_group && !tmp.children?.length) return acc;
      acc.push(tmp);
    }
    return acc;
  }, []);
}

/**
 * Remonte au niveau racine du routeur les modules enveloppés dans un nœud de
 * regroupement sidebar (`meta.is_group`). L'arbre routeur reste ainsi identique
 * à la version non groupée (URLs, `matched`/breadcrumb, permissions inchangés) :
 * le regroupement est purement présentationnel côté menu.
 */
function expandGroups(routes) {
  return routes.flatMap(route =>
    route.meta?.is_group && route.children?.length ? route.children : [route]
  );
}

/**
 * Aplatit les routes pour Vue Router.
 * Les nœuds sans `component` (regroupement sidebar) voient leurs enfants
 * remontés au parent avec le path préfixé.
 */
function flattenForRouter(routes) {
  return routes.map(route => {
    if (!route.children) return route;

    const flat = { ...route };
    const flatChildren = [];

    for (const child of route.children) {
      if (!child.component && child.children) {
        for (const grandchild of child.children) {
          const prefix = child.path || '';
          const suffix = grandchild.path || '';
          flatChildren.push({
            ...grandchild,
            path: prefix && suffix ? `${prefix}/${suffix}` : prefix || suffix,
          });
        }
      } else {
        flatChildren.push(child);
      }
    }

    flat.children = flatChildren;
    return flat;
  });
}

const state = () => ({
  routes: [],
  addRoutes: []
});

const mutations = {
  SET_ROUTES(state, routes) {
    state.addRoutes = routes;
    state.routes = constantRoutes.concat(routes);
  }
};

const actions = {
  async generateRoutes({ commit }, { roles = [], permissions = [], isExtranet = false } = {}) {
    try {
      const accessedRoutes = filterAsyncRoutes(asyncRoutes, roles, permissions, isExtranet);

      const uniqueRoutes = [];
      const pathsSet = new Set();
      accessedRoutes.forEach(route => {
        if (!pathsSet.has(route.path)) {
          pathsSet.add(route.path);
          uniqueRoutes.push(route);
        }
      });

      // Structure imbriquée + groupée pour la sidebar
      commit('SET_ROUTES', uniqueRoutes);
      // Structure aplatie et dégroupée pour Vue Router (pas de nœud intermédiaire)
      return flattenForRouter(expandGroups(uniqueRoutes));
    } catch (error) {
      console.error('Error generating routes:', error);
      return [];
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
